import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { RiMoonLine, RiSunLine } from 'react-icons/ri';

function getMarketingBase() {
  const { protocol, hostname, port } = window.location;
  const host = hostname.replace(/^app\./, '');
  return `${protocol}//${host}${port ? ':' + port : ''}`;
}

export default function Navbar() {const { t } = useTranslation();
  const base = getMarketingBase();
  const NAV_LINKS = [
    { key: 'solutions', href: `${base}/solutions` },
    { key: 'nav_features', href: `${base}/features` },
    { key: 'nav_pricing', href: `${base}/pricing` },
    { key: 'nav_about', href: `${base}/about` },
  ];
  const { isDarkMode, toggleDarkMode, isRTL } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const menuRef = useRef(null);

  /* scroll */
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  /* close on outside click */
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  /* lock body scroll */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {document.body.style.overflow = '';};
  }, [menuOpen]);

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
            <style>{`
        .nb-link {
          position: relative;
          color: ${isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.85)'};
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.2px;
          text-decoration: none;
          transition: color 0.25s ease;
          padding: 2px 0;
        }
        .nb-link::after {
          content: '';
          position: absolute;
          bottom: -2px; left: 0; right: 0;
          height: 1px;
          background: ${isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'};
          transform: scaleX(0);
          transform-origin: ${isRTL ? 'right' : 'left'};
          transition: transform 0.25s ease;
          border-radius: 1px;
        }
        .nb-link:hover, .nb-link.active { color: ${isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'}; }
        .nb-link:hover::after, .nb-link.active::after { transform: scaleX(1); }

        .nb-cta {
          color: ${isDarkMode ? 'white' : 'white'};
          background: ${isDarkMode ? 'transparent' : '#c41e1e'};
          font-size: 13px;
          font-weight: 500;
          padding: 7px 18px;
          border-radius: 100px;
          text-decoration: none;
          letter-spacing: 0.2px;
          border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : 'transparent'};
          transition: border-color 0.25s, background 0.25s;
          white-space: nowrap;
        }
        .nb-cta:hover {
          border-color: ${isDarkMode ? 'rgba(255,255,255,0.28)' : 'transparent'};
          background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : '#a91818'};
        }

        .nb-signin {
          color: ${isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.85)'};
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.2px;
          text-decoration: none;
          transition: color 0.25s;
          white-space: nowrap;
        }
        .nb-signin:hover { color: ${isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'}; }

        /* ── Mobile overlay ─────────────────────────── */
        .nb-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.4);
          backdrop-filter: blur(4px);
          z-index: 59;
          opacity: 0; pointer-events: none;
          transition: opacity 0.3s ease;
        }
        .nb-overlay.on { opacity: 1; pointer-events: auto; }

        /* ── Mobile drawer ──────────────────────────── */
        .nb-drawer {
          position: fixed; top: 0; ${isRTL ? 'left' : 'right'}: 0;
          width: 320px; height: 100dvh;
          background: ${isDarkMode ? 'rgba(10,10,11,0.98)' : '#fff'};
          backdrop-filter: blur(32px);
          border-${isRTL ? 'right' : 'left'}: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};
          z-index: 60;
          transform: translateX(${isRTL ? '-100%' : '100%'});
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          display: flex; flex-direction: column;
        }
        .nb-drawer.open { transform: translateX(0); }

        .nb-mlink-wrap {
            flex: 1;
            padding: 40px 0;
            display: flex; flex-direction: column;
        }

        .nb-mlink {
          display: flex;
          align-items: center;
          gap: 16px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)'};
          font-size: 15px; font-weight: 400; letter-spacing: 0.1px;
          text-decoration: none;
          padding: 16px 40px;
          transition: all 0.2s ease;
        }
        .nb-mlink:hover {
          color: ${isDarkMode ? '#fff' : '#000'};
          background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'};
        }

        .nb-m-footer {
            padding: 32px 40px;
            background: ${isDarkMode ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)'};
        }

        /* ── Hamburger ──────────────────────────────── */
        .hb {
            width: 20px; height: 14px;
            display: flex; flex-direction: column; justify-content: space-between;
        }
        .hb-line {
          display: block; height: 1.2px; width: 100%;
          background: ${isDarkMode ? '#fff' : '#000'};
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hb.open .hb-line:nth-child(1) { transform: translateY(6.4px) rotate(45deg); }
        .hb.open .hb-line:nth-child(2) { opacity: 0; }
        .hb.open .hb-line:nth-child(3) { transform: translateY(-6.4px) rotate(-45deg); }

        /* ── Theme toggle ──────────────────────────── */
        .theme-toggle-btn {
            width: 36px; height: 36px;
            display: flex; align-items: center; justify-content: center;
            border-radius: 50%;
            transition: background 0.3s ease;
            color: ${isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.6)'};
        }
        .theme-toggle-btn:hover {
          background: ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
          color: ${isDarkMode ? '#fff' : '#000'};
        }
      `}</style>

            {/* ── Outer wrapper ─ handles the floating inset when scrolled ─ */}
            <div
        className="fixed inset-x-0 top-0 z-50 pointer-events-none"
        style={{
          padding: scrolled ? '18px 24px 0' : '0',
          transition: 'padding 0.45s cubic-bezier(0.4,0,0.2,1)'
        }}>

                {/* ── Nav pill / bar ────────────────────────────────────────── */}
                <nav
          className="pointer-events-auto w-full mx-auto flex items-center"
          style={{
            height: scrolled ? '68px' : '72px',
            maxWidth: scrolled ? '1200px' : '100%',
            borderRadius: scrolled ? '30px' : '0',
            background: scrolled ?
            isDarkMode ? 'rgba(13,13,13,0.78)' : 'rgba(255,255,255,0.85)' :
            'transparent',
            backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
            border: scrolled ?
            isDarkMode ? '1px solid rgba(255,255,255,0.075)' : '1px solid rgba(0,0,0,0.08)' :
            '1px solid transparent',
            boxShadow: scrolled ?
            isDarkMode ?
            '0 2px 20px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.03) inset' :
            '0 4px 24px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02) inset' :
            'none',
            padding: scrolled ? '0 28px' : '0 40px',
            transition: 'all 0.45s cubic-bezier(0.4,0,0.2,1)'
          }}>

                    {/* Logo + brand name */}
                    <Link to="/" className="flex items-center gap-2.5 shrink-0 group" onClick={() => setMenuOpen(false)}>
                        <img
              src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
              alt="QuadProperties"
              className="w-auto object-contain transition-all duration-300 group-hover:opacity-85"
              style={{ height: scrolled ? '28px' : '32px', transition: 'height 0.4s cubic-bezier(0.4,0,0.2,1)' }} />

                        <span className="hidden sm:inline" style={{
              fontSize: 13,
              fontWeight: 400,
              color: isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.85)',
              letterSpacing: '-0.2px',
              transition: 'opacity 0.3s',
              whiteSpace: 'nowrap'
            }}>{t("quadcores_properties_2")}</span>
                    </Link>

                    {/* Desktop center links */}
                    <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
                        {NAV_LINKS.map(({ key, href }) =>
            <a
              key={key}
              href={href}
              className={`nb-link ${activeLink === href ? 'active' : ''}`}
              onClick={() => setActiveLink(href)}>
              {t(key)}
            </a>
            )}
                        <Link to="/portal/tenant/login" className="nb-link">{t("tenant_login")}</Link>
                        <Link to="/portal/owner/login" className="nb-link">{t("owner_login")}</Link>
                    </div>

                    {/* Desktop right CTAs */}
                    <div className="hidden md:flex items-center gap-5 shrink-0">
                        <button
              onClick={toggleDarkMode}
              className="theme-toggle-btn"
              aria-label="Toggle theme">

                            {isDarkMode ? <RiSunLine size={18} /> : <RiMoonLine size={18} />}
                        </button>
                        <Link to="/login" className="nb-signin">{t("sign_in")}</Link>
                        <Link to="/register" className="nb-cta">{t("get_started_1")}</Link>
                    </div>

                    {/* Mobile: sign-in + hamburger */}
                    <div className="flex md:hidden items-center gap-4 ms-auto">
                        <button
              onClick={toggleDarkMode}
              className="theme-toggle-btn"
              aria-label="Toggle theme">

                            {isDarkMode ? <RiSunLine size={18} /> : <RiMoonLine size={18} />}
                        </button>
                        <Link to="/login" className="nb-signin">{t("sign_in_1")}</Link>
                        <button
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((v) => !v)}
              className={`hb ${menuOpen ? 'open' : ''} bg-transparent border-none cursor-pointer p-0`}>

                            <span className="hb-line" />
                            <span className="hb-line" style={{ width: '100%', marginTop: '5px' }} />
                            <span className="hb-line" style={{ marginTop: '5px' }} />
                        </button>
                    </div>
                </nav>
            </div>

            {/* ── Mobile Overlay & Drawer ────────────────────────────────── */}
            <div
        className={`nb-overlay ${menuOpen ? 'on' : ''}`}
        onClick={() => setMenuOpen(false)} />


            <div className={`nb-drawer ${menuOpen ? 'open' : ''}`} ref={menuRef}>
                {/* Drawer Header */}
                <div className={`flex items-center justify-between px-8 py-7 border-b ${isDarkMode ? 'border-white/[0.05]' : 'border-black/[0.05]'}`}>
                    <div className="flex items-center gap-3">
                        <img src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"} alt="QuadProperties" className="h-6 w-auto" />
                        <span style={{ fontSize: 13, fontWeight: 500, color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.8)', letterSpacing: '-0.1px' }}>{t("menu")}</span>
                    </div>
                    <button
            onClick={() => setMenuOpen(false)}
            className={`w-8 h-8 rounded-full flex items-center justify-center border ${isDarkMode ? 'border-white/10 hover:bg-white/[0.06] text-white/50' : 'border-black/10 hover:bg-black/[0.06] text-black/50'} transition-colors`}>

                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Staggered Links */}
                <div className="nb-mlink-wrap">
                    {NAV_LINKS.map(({ key, href }) =>
          <a
            key={key}
            href={href}
            className="nb-mlink"
            onClick={() => setMenuOpen(false)}>

                                <span className={`w-1 h-1 rounded-full ${isDarkMode ? 'bg-white/20' : 'bg-black/20'}`} />
                                {t(key)}
                            </a>
          )}
                </div>

                {/* Footer CTAs */}
                <div className="nb-m-footer">
                    <div className="flex flex-col gap-3">
                        <Link
              to="/register"
              className="w-full py-3.5 bg-red-700 hover:bg-red-600 text-white text-[13px] font-semibold text-center rounded-xl transition-all shadow-lg shadow-red-900/10"
              onClick={() => setMenuOpen(false)}>
                            {t("get_started_2")}

            </Link>

                        <div className="grid grid-cols-2 gap-2">
                            <Link
                to="/portal/tenant/login"
                className={`py-3 text-[11px] font-medium text-center border ${isDarkMode ? 'border-white/5 text-white/40 hover:text-white/70' : 'border-black/5 text-black/50 hover:text-black/80'} rounded-xl transition-all`}
                onClick={() => setMenuOpen(false)}>
                                {t("tenant_13")}

              </Link>
                            <Link
                to="/portal/owner/login"
                className={`py-3 text-[11px] font-medium text-center border ${isDarkMode ? 'border-white/5 text-white/40 hover:text-white/70' : 'border-black/5 text-black/50 hover:text-black/80'} rounded-xl transition-all`}
                onClick={() => setMenuOpen(false)}>
                                {t("owner_1")}

              </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>);

}
